import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import FA from "../../fa";
import {
  INK,
  MUTED,
  GRID,
  tierColor,
  SUBJECT_COLORS,
  SUBJECT_LABELS,
} from "../tokens";
import { seasonRateDetail, seasonShadeDetail } from "../schoolyear";
import { fmtBucketLabel } from "../format";
import {
  chartOptions,
  categoryScales,
  axisTitle,
  MAX_BAR_THICKNESS,
} from "../charts";
import {
  ChartCard,
  SegControl,
  Legend,
  PlotNote,
  SectionHead,
} from "../ui";
import { CYCLE_BAND_FILL, CYCLE_BAND_STROKE } from "../primitives";

// The dose→response's evidence footer: under each column, a small bar for how
// many learners it stands on.
//
// A footer rather than a marimekko whose columns are width-scaled by tier roster.
// That encoding is right about the problem — the usage tiers are wildly unequal
// and the interesting ones are the small ones (on prod, Bridges Prep's "Intensive"
// tier holds 28 learners against 103 in "Minimal"), so at equal widths a
// six-learner tier's mastery average carries the same visual authority as a
// hundred-learner one and reads as a finding rather than as an anecdote — but it
// is wrong about the cost. x IS dose here, so stretching it squeezes the high
// bands into slivers against the right edge, and the *shape* of the response,
// which is the only thing this plot exists to show, stops being readable.
//
// Splitting the two encodings keeps both: equal-width columns for the shape,
// proportional bars beneath the axis for the evidence. It also gives the Usage
// lens one idiom — every plot on it carries its own denominator directly under its
// own axis (the mix strip's reach strip does the same job one panel up).
//
// Drawn from each bar element's own x/width, so a footer bar sits exactly under
// the column it describes even with two subjects grouped, and it survives
// animation and resize because afterDatasetsDraw runs every frame.
const EVIDENCE_H = 18;
const EVIDENCE_PAD = 40; // reserved under the axis: strip + count labels
const EvidenceBars = {
  id: "evidenceBars",
  afterDatasetsDraw(chart) {
    const cfg = chart.options.plugins && chart.options.plugins.evidenceBars;
    if (!cfg || !cfg.rosters || !cfg.rosters.length) return;
    const { rosters, colors = [] } = cfg;
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x) return;
    const max = rosters.reduce(
      (m, ds) => Math.max(m, ...ds.map((n) => n || 0)),
      0
    );
    if (!max) return;
    const top = chartArea.bottom + scales.x.height + 4;
    ctx.save();
    // The baseline the footer bars stand on, so a tier with no learners reads as
    // an empty slot rather than as a gap in the drawing.
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(chartArea.left, top + EVIDENCE_H + 0.5);
    ctx.lineTo(chartArea.right, top + EVIDENCE_H + 0.5);
    ctx.stroke();
    ctx.font = "10px -apple-system, Segoe UI, Roboto, sans-serif";
    ctx.fillStyle = MUTED;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("learners", chartArea.left - 8, top + EVIDENCE_H / 2);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    rosters.forEach((ds, d) => {
      const meta = chart.getDatasetMeta(d);
      ds.forEach((n, b) => {
        const el = (meta.data || [])[b];
        if (!el) return;
        const w = Math.max(2, el.width || 0);
        // a floor of 1.5px: one learner in a tier of hundreds still has to leave
        // a mark, or "tiny sample" is indistinguishable from "no sample"
        const h = n ? Math.max(1.5, (n / max) * EVIDENCE_H) : 0;
        if (h) {
          ctx.fillStyle = (colors[d] || INK) + "66";
          ctx.fillRect(el.x - w / 2, top + EVIDENCE_H - h, w, h);
        }
        // the count itself where the column is wide enough to hold it; the
        // tooltip carries it in every case. Zeros are left off — a dozen "0"s
        // under the empty high bands is noise, and an empty slot already says it.
        if (n && w >= 26) {
          ctx.fillStyle = MUTED;
          ctx.fillText(String(n), el.x, top + EVIDENCE_H + 5);
        }
      });
    });
    ctx.restore();
  },
};

// Stacked "engagement mix over time": what share of learners were at each usage
// tier (or with no lessons) per bucket. The learner-side analogue of the robot-
// utilization fleet strip, and the lower panel of the Usage lens's pair -- the
// ribbon above it says WHEN and WHERE the usage happened, this says HOW MUCH each
// learner got out of it. Tiers/labels/colors come entirely from the backend
// manifest (comp.tiers), so a config-side bin change flows through with no edit
// here. NOTE: this uses per-bucket usage (zero lessons in a bucket == "no lessons"
// for that bucket), a different reference window than the whole-window dose-response
// below -- so the leftover counts here won't match; that's expected (evolution vs.
// final state).
//
// "Show" defaults to WITH LESSONS: the "no lessons" band is ~80% of every bar on the
// largest district, which leaves the usage bands -- the actual subject of the plot --
// as a sliver along the bottom. What that default gives up is the full-roster
// denominator, which the reach strip under the axis carries instead (see
// reachStripPlugin). "All learners" is one click away for the reading where the
// leftover band IS the finding.
//
// `paired` means this is drawn directly under the adoption ribbon: it takes the
// panel caption rather than a section heading, and pins its plot area to the
// ribbon's gutters so the two panels share one week axis.
//
// Both of this chart's controls live in its own header rather than in the page's
// control bar, because both are about how this one plot is drawn and neither
// touches the dose-response below (that reads the window's endpoints, not its
// buckets). The frame stays mounted while a refetch is in flight so the controls
// don't jump around -- and so the "Show" selection survives it.
//
// Tints the buckets inside a detected focus cycle, behind the bars.
//
// The point is to make the ANNOTATION form of focus cycles carry weight without
// costing sample size. Selecting a cycle in the Period control narrows the window
// — useful, but it throws away the rest of the year — whereas this shows where the
// pushes were while every bar still describes the full window. Same fill and same
// outline as the ribbon's per-school cycles, so a reader who has seen one
// recognizes the other.
//
// Modelled on the fleet strip's out-of-session shading: a beforeDraw plugin
// painting bucket-wide columns under the datasets, so it survives animation and
// resize and needs no extra dataset competing in the legend.
const cycleShadePlugin = (bands, fill, stroke) => ({
  id: "cycleShade",
  beforeDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x || !bands || !bands.length) return;
    const xs = scales.x;
    const count = chart.data.labels.length || 1;
    const half = xs.width / count / 2;
    ctx.save();
    const h = chartArea.bottom - chartArea.top;
    bands.forEach(([lo, hi]) => {
      const a = xs.getPixelForValue(lo) - half;
      const b = xs.getPixelForValue(hi) + half;
      ctx.fillStyle = fill;
      ctx.fillRect(a, chartArea.top, b - a, h);
      // Same fill AND same outline as the ribbon's per-school cycles one panel
      // up, so the annotation is learned once rather than twice.
      if (stroke) {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1;
        ctx.strokeRect(a + 0.5, chartArea.top + 0.5, b - a - 1, h - 1);
      }
    });
    ctx.restore();
  },
});

// ---- the mix, as a bare chart + the small models around it ------------------
// Kept separate from EngagementCompositionStrip so the merged usage panel can put
// this canvas in a card it shares with the adoption ribbon, under captions and
// controls the panel owns. The standalone wrapper below draws the same chart with
// its own heading, legend and card.

// Usage bands, best-used first: stacked at the baseline (like active-at-baseline
// on Robots), then the non-dose buckets (no lessons / out of session) on top.
const mixBands = (tiers = []) =>
  tiers.filter((t) => t.kind === "band").sort((a, b) => b.order - a.order);

const mixStack = (tiers = [], engagedOnly) => [
  ...mixBands(tiers),
  ...(engagedOnly
    ? []
    : tiers.filter((t) => t.kind !== "band").sort((a, b) => a.order - b.order)),
];

// Legend in natural reading order (low usage -> high -> churned), and only for
// what is actually drawn.
const mixLegend = (tiers, engagedOnly) =>
  [...mixStack(tiers, engagedOnly)]
    .sort((a, b) => a.order - b.order)
    .map((t) => ({ color: tierColor(t), label: t.label }));

// The denominator, as a scalar rather than as a series.
//
// A per-bucket "share of roster" series would be redundant here: enrollment
// barely moves across a year, so share-of-roster is the mix's own bar heights
// with the y squashed. What the reader actually can't get from the bars is the
// *size* of what was left out, which is one number.
const mixReach = (rows = [], tiers = []) => {
  const bands = mixBands(tiers);
  let peak = 0;
  let roster = 0;
  rows.forEach((row) => {
    const engaged = bands.reduce((a, t) => a + (row[t.key] || 0), 0);
    const total = Object.values(row).reduce((a, n) => a + n, 0);
    if (engaged > peak) peak = engaged;
    if (total > roster) roster = total;
  });
  return { peak, roster };
};

// `gutter` pins the chart area to the ribbon's label/total columns so the two
// panels share one timeline; `showXLabels` is false for the upper panel of the
// merged card, where the axis is drawn once at the bottom.
const MixChart = ({
  comp,
  engagedOnly,
  cycleBands,
  gutter,
  showXLabels = true,
}) => {
  const {
    buckets = [],
    composition_by_bucket: rows = [],
    tiers = [],
    interval,
  } = comp || {};
  const stack = mixStack(tiers, engagedOnly);
  // Buckets the window cut short -- the trailing one, since the window ends
  // today. A dosage mix over three days of a month is a real reading of three
  // days and not comparable to the month beside it: nearly every learner lands in
  // the bottom band simply because the month has barely happened. Faded and
  // called out in the tooltip rather than dropped, since it is also the only
  // column showing this week.
  const partial = buckets.map((b) => !!b.partial);
  const data = {
    labels: buckets.map((b) => fmtBucketLabel(b.start, interval)),
    datasets: stack.map((t) => ({
      label: t.label,
      data: rows.map((c) => c[t.key] || 0),
      backgroundColor: partial.some(Boolean)
        ? buckets.map(
            (_, i) =>
              tierColor(t) +
              (partial[i] ? "59" : t.min_weekly == null ? "b3" : "d9")
          )
        : tierColor(t) + (t.min_weekly == null ? "b3" : "d9"),
      borderColor: "#fff",
      borderWidth: 1,
      borderRadius: 3,
      maxBarThickness: MAX_BAR_THICKNESS,
    })),
  };
  const options = chartOptions({
    interaction: { mode: "index", intersect: false },
    layout: { padding: { right: gutter ? gutter.right : 0 } },
    plugins: {
      tooltip: {
        callbacks: {
          footer: (items) => {
            const i = items.length ? items[0].dataIndex : 0;
            const row = rows[i] || {};
            // the roster, always — the hidden buckets are still learners
            const total = Object.values(row).reduce((a, n) => a + n, 0);
            const note = partial[i]
              ? `This ${interval} is still in progress — a partial period`
              : null;
            const lines = [];
            if (!engagedOnly) lines.push(`${total} learners`);
            else {
              const shown = stack.reduce((a, t) => a + (row[t.key] || 0), 0);
              const pct = total ? Math.round((100 * shown) / total) : 0;
              lines.push(`${shown} of ${total} learners took a lesson (${pct}%)`);
            }
            if (note) lines.push(note);
            return lines.length > 1 ? lines : lines[0];
          },
        },
      },
    },
    scales: categoryScales({
      x: {
        stacked: true,
        // The tick color comes from categoryScales; only the layout of the labels
        // is this panel's business. When they are off the merged object still
        // carries that color behind `display: false`, where Chart.js never reads
        // it -- the strip is drawn without labels either way.
        ticks: showXLabels
          ? { maxRotation: 0, autoSkipPadding: 14 }
          : { display: false },
      },
      y: {
        stacked: true,
        title: axisTitle(engagedOnly ? "learners with lessons" : "learners"),
        ticks: { precision: 0 },
        // Widen the axis area to the ribbon's school-name column so both panels
        // start their weeks at the same x.
        afterFit: gutter
          ? (scale) => {
              scale.width = gutter.left;
            }
          : undefined,
      },
    }),
  });
  const plugins =
    cycleBands && cycleBands.length
      ? [cycleShadePlugin(cycleBands, CYCLE_BAND_FILL, CYCLE_BAND_STROKE)]
      : [];
  return <Bar data={data} options={options} plugins={plugins} />;
};

// Shared reading note for the mix, used by the standalone strip and by the merged
// panel (which appends the ribbon's half of the picture to it).
const MixNoteDetails = ({ comp, engagedOnly, interval, cycleBands, cycleLabel }) => (
  <>
    Tiers are lessons per week against the ≈2/week recommendation.{" "}
    {/* Read off `comp` rather than passed in: the strip is drawn from that
        response, so the caveat and the bars can't disagree about which window's
        season they describe. Absent under a cycle, whose measured weeks replace
        the calendar entirely. */}
    {seasonRateDetail(comp && comp.season) && (
      <>{seasonRateDetail(comp.season)} </>
    )}
    {seasonShadeDetail(comp && comp.season) && (
      <>{seasonShadeDetail(comp.season)} </>
    )}
    {engagedOnly
      ? "Learners with no lessons are left out of the bars, so bar height is the engaged roster — read the mix, not the size. Switch to “All learners” to put the rest back in the bar and see how thin a slice of the district it is."
      : "The denominator is the full enrolled roster, so a bar that is mostly “no lessons” reframes any mastery gain below: it is coming from a thin slice of the district."}
    {interval === "week" && (
      <>
        {" "}
        At weekly grain the low bands empty out — one lesson in a single week
        already scores at or above 1/wk, so a light user reads as “approaching” in
        the weeks they show up and as “no lessons” in the weeks they don't.
      </>
    )}
    {cycleBands && cycleBands.length ? (
      <>
        {" "}
        The outlined {cycleBands.length > 1 ? "bands mark" : "band marks"}{" "}
        {cycleLabel || "the detected focus cycle"} — every bar still counts the
        whole window, so this only says when the push was.
      </>
    ) : null}
  </>
);

// The mix's own controls, owned by whichever header is drawing them.
const MixControls = ({ grain, onGrainChange, interval, show, setShow }) => (
  <>
    {onGrainChange && (
      <SegControl
        inline
        label="Grain"
        value={grain || interval}
        onChange={onGrainChange}
        options={[
          ["month", "Monthly"],
          ["week", "Weekly"],
        ]}
      />
    )}
    <SegControl
      inline
      label="Show"
      value={show}
      onChange={setShow}
      options={[
        ["engaged", "With lessons"],
        ["all", "All learners"],
      ]}
    />
  </>
);

// The standalone form: its own heading, legend, card and note. Used where the mix
// is a section in its own right — the Usage lens inside a focus cycle, where the
// ribbon is suppressed and there is nothing to pair it with.
const EngagementCompositionStrip = ({
  comp,
  grain,
  onGrainChange,
  loading,
  failed,
}) => {
  const [show, setShow] = useState("engaged");
  const ready = !!(comp && comp.tiers && comp.composition_by_bucket);
  if (!ready && !loading && !failed) return null;
  const { tiers = [], interval } = comp || {};
  const engagedOnly = show === "engaged";
  return (
    <div style={{ margin: "0 0 20px" }}>
      <SectionHead
        title="Engagement mix over time"
        controls={
          <MixControls
            grain={grain}
            onGrainChange={onGrainChange}
            interval={interval}
            show={show}
            setShow={setShow}
          />
        }
      />
      {failed && (
        <div style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
          Couldn’t load the engagement mix for this period.
        </div>
      )}
      {ready && (
        <>
          <Legend items={mixLegend(tiers, engagedOnly)} />
          <ChartCard height={200}>
            <MixChart comp={comp} engagedOnly={engagedOnly} />
          </ChartCard>
          <PlotNote
            style={{ margin: "10px 0 0" }}
            details={
              <MixNoteDetails
                comp={comp}
                engagedOnly={engagedOnly}
                interval={interval}
              />
            }
          >
            Share of {engagedOnly ? "engaged learners" : "the roster"} at each
            usage tier, per {interval || "period"}.
            {comp.buckets &&
            comp.buckets.length &&
            comp.buckets[comp.buckets.length - 1].partial ? (
              <>
                {" "}
                The faded final bar is{" "}
                {interval === "week" ? "a week" : "a month"} still in progress —
                a partial period puts learners in low tiers simply because
                little of it has happened yet.
              </>
            ) : null}
          </PlotNote>
        </>
      )}
      {!ready && loading && (
        <ChartCard height={200}>
          <div style={{ color: MUTED, fontSize: 13, padding: 8 }}>
            <FA color="gray" icon="spinner" spin /> Rebuilding the mix…
          </div>
        </ChartCard>
      )}
    </div>
  );
};

// Dose-response: standards mastered per learner, grouped by dosage tier. The
// *shape* is the point, not the (truistic) upward direction -- where does the
// payoff flatten relative to the ≈2/wk recommendation? Columns are equal width so
// that shape survives; how many learners each column stands on is drawn under the
// axis instead (see EvidenceBars).
//
// Broken down by subject: `subjectSchools` holds one engagement-segmented bundle
// per subject (MATH / READING), each already subject-scoped server-side on BOTH
// axes -- a learner is binned by *that subject's* dosage and the bar shows *that
// subject's* mastery. That's why the split has to come from separate bundles and
// not a client-side filter: a subject-blind x-axis crossed with one subject's
// mastery would mislead (a math-heavy learner would drag down the reading curve
// at high "dose" despite doing no reading). The selector picks Math, Reading, or
// both (grouped bars). The "no lessons" bucket is excluded per subject: it's the
// absence of dose, not a band, so it doesn't belong on a dose axis.
const DoseResponseChart = ({
  subjectSchools,
  tiers,
  recommendedWeekly,
  season,
}) => {
  const [subjectSel, setSubjectSel] = useState("both");
  if (!tiers || !subjectSchools) return null;

  const bands = tiers
    .filter((t) => t.min_weekly != null)
    .sort((a, b) => a.order - b.order);
  const activeSubjects =
    subjectSel === "both" ? ["MATH", "READING"] : [subjectSel];

  // per active subject: one value + tier roster per band (each subject has its
  // own tier membership, so rosters differ across subjects)
  const seriesBySubject = activeSubjects.map((subj) => {
    const schools = subjectSchools[subj] || [];
    const rows = bands.map((t) => {
      const seg = schools.find((s) => s.id === `engagement:${t.key}`);
      const cur = seg && seg.current;
      // perLearnerInWindow == in-window mastery gain / in-window engaged
      // learners (the same per-learner basis the trajectory lines use)
      return {
        value: cur ? cur.perLearnerInWindow : null,
        roster: seg ? seg.roster : 0,
      };
    });
    return { subj, rows };
  });

  const selector = (
    <SegControl
      inline
      label="Subject"
      value={subjectSel}
      onChange={setSubjectSel}
      options={[
        ["both", "Math + Reading"],
        ["MATH", "Math"],
        ["READING", "Reading"],
      ]}
    />
  );

  // Shape control on the heading line rather than on a line of its own: it
  // redraws this plot and nothing else, and the page's control bar is reserved
  // for what changes the data on screen.
  const heading = <SectionHead title="Dose → response" controls={selector} />;

  const note = (
    <PlotNote
      style={{ margin: "10px 0 0" }}
      details={
        <>
          Each learner is placed by how many <em>math</em> (or <em>reading</em>)
          lessons/week they average, and the bar shows that same subject's
          mastery — so one learner can sit in different tiers on the two series.{" "}
          <strong>
            The small bar under each column is how many learners it stands on
          </strong>
          , scaled against the biggest tier on the chart: a tall column over a
          stub is an anecdote, not a finding. Read the <em>shape</em>, not the
          direction: if gains flatten at{" "}
          {recommendedWeekly
            ? `${recommendedWeekly}/week`
            : "the recommended dose"}
          , the recommendation is validated; if they keep climbing past it, it
          may be set too low. This is an internal targeting view, not an efficacy
          claim — higher-dose learners self-select, so the gap isn't purely
          causal.
          {/* Belongs here specifically: which divisor placed a learner decides
              which column they are in, so it is a reading instruction for the
              x-axis rather than a general caveat. */}
          {seasonRateDetail(season) ? ` ${seasonRateDetail(season)}` : ""}
        </>
      }
    >
      Standards mastered per learner by that subject's dosage tier; the strip
      below the axis is the learners behind each column.
    </PlotNote>
  );

  const anyData = seriesBySubject.some((s) =>
    s.rows.some((r) => r.value != null)
  );
  if (!anyData)
    return (
      <div style={{ margin: "24px 0 0" }}>
        {heading}
        <div style={{ color: "#52514e" }}>
          No per-learner mastery in the selected subject for this window.
        </div>
      </div>
    );

  // Short dose ranges ("1-2/wk") rather than full tier names on the axis: x IS
  // dose, so the range is the more useful label, and the full name is one hover
  // away in the tooltip title.
  const bandLabels = bands.map((t) => {
    const m = /\(([^)]+)\)/.exec(t.label);
    return m ? m[1] : t.label;
  });
  const subjectColor = (subj) =>
    SUBJECT_COLORS[subj] || tierColor({ kind: "band", order: 0 });
  const data = {
    labels: bandLabels,
    datasets: seriesBySubject.map((s) => ({
      label: SUBJECT_LABELS[s.subj] || s.subj,
      data: s.rows.map((r) => r.value),
      backgroundColor: subjectColor(s.subj) + "d9",
      borderColor: "#fff",
      borderWidth: 1,
      borderRadius: 4,
      maxBarThickness: 64,
    })),
  };
  // Per-dataset, per-band learner counts drive the evidence bars under the axis.
  const rosters = seriesBySubject.map((s) => s.rows.map((r) => r.roster || 0));
  const totalBinned = rosters.reduce(
    (a, ds) => a + ds.reduce((b, n) => b + n, 0),
    0
  );
  const options = chartOptions({
    layout: { padding: { bottom: EVIDENCE_PAD } },
    plugins: {
      // chart.js legend does the subject color key when >1 series is shown, so
      // this is one of the few places that overrides the frame's legend: false
      legend: {
        display: activeSubjects.length > 1,
        position: "top",
        align: "end",
        labels: { boxWidth: 12, boxHeight: 12, color: MUTED },
      },
      evidenceBars: {
        rosters,
        colors: activeSubjects.map((subj) => subjectColor(subj)),
      },
      tooltip: {
        callbacks: {
          // the tier's full name, since the axis carries only its range
          title: (items) =>
            items.length ? bands[items[0].dataIndex].label : "",
          label: (ctx) => {
            const subj = SUBJECT_LABELS[activeSubjects[ctx.datasetIndex]] || "";
            const v = ctx.parsed.y;
            return `${subj}: ${v == null ? "—" : v.toFixed(2)} mastered / learner`;
          },
          footer: (items) => {
            if (!items.length) return "";
            const it = items[0];
            const s = seriesBySubject[it.datasetIndex];
            const r = s && s.rows[it.dataIndex];
            if (!r) return "";
            const share = totalBinned ? (100 * r.roster) / totalBinned : 0;
            return `${r.roster} learners in tier (${share.toFixed(
              share < 1 ? 1 : 0
            )}% of those shown)`;
          },
        },
      },
    },
    // No x axis title: the tick labels are dose ranges and say so themselves,
    // and a title here would land between the ticks and the evidence strip.
    scales: categoryScales({
      y: { title: axisTitle("standards mastered / learner (in window)") },
    }),
  });
  return (
    <div style={{ margin: "24px 0 0" }}>
      {heading}
      <ChartCard height={320 + EVIDENCE_PAD}>
        <Bar data={data} options={options} plugins={[EvidenceBars]} />
      </ChartCard>
      {note}
    </div>
  );
};

// ---- Engagement tab --------------------------------------------------------
// The learner-usage views (mix-over-time + dose-response), kept off the
// Trajectory tab because they don't move with the school/teacher/grade breakdown
// the way the trajectory lines do -- they're always segmented by usage tier, so
// grouping them there would be misleading. Loaded lazily (see the engagement
// effect).
const EngagementTab = ({ eng }) => {
  if (eng.awaitingOrg)
    return (
      <div style={{ color: "#52514e" }}>
        Select an organization above to view engagement.
      </div>
    );
  if (eng.loading || (!eng.comp && !eng.subjectSchools))
    return (
      <div style={{ color: "#52514e" }}>
        <FA color="gray" icon="spinner" spin /> Loading engagement…
      </div>
    );
  const hasComp = eng.comp && eng.comp.composition_by_bucket;
  const hasDose =
    eng.tiers &&
    eng.subjectSchools &&
    ["MATH", "READING"].some(
      (k) => (eng.subjectSchools[k] || []).length > 0
    );
  if (!hasComp && !hasDose)
    return (
      <div style={{ color: "#52514e" }}>
        No engagement data for this window — confirm learners have lessons
        (SyncUserProfile rows) in the selected school year.
      </div>
    );
  return (
    <>
      {/* `usagePanel` is the merged ribbon+mix card, supplied by the page that
          has the ribbon's api/org/window to hand. Inside a focus cycle there is
          no ribbon to merge with, so the mix falls back to its standalone form. */}
      {eng.usagePanel ? (
        eng.usagePanel
      ) : (
        <EngagementCompositionStrip
          comp={eng.comp}
          grain={eng.grain}
          onGrainChange={eng.onGrainChange}
          loading={eng.compLoading}
          failed={eng.compFailed}
        />
      )}
      <DoseResponseChart
        subjectSchools={eng.subjectSchools}
        tiers={eng.tiers}
        recommendedWeekly={eng.recommendedWeekly}
        // From the segmented (dose) response, not the strip's: the two are
        // fetched separately and under a cycle only one of them carries a season.
        season={eng.season}
      />
    </>
  );
};

export {
  EngagementTab,
  MixChart,
  MixControls,
  MixNoteDetails,
  mixLegend,
  mixReach,
};
